import { type LucideIcon,MoreVertical } from 'lucide-react'
import { useEffect,useRef, useState } from 'react'
import { createPortal } from 'react-dom'

export interface ActionMenuItem {
  label: string
  icon?: LucideIcon
  onClick: () => void
  variant?: 'default' | 'destructive'
}

interface ActionMenuProps {
  items: ActionMenuItem[]
}

const ActionMenu = ({ items }: ActionMenuProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const anchorRef = useRef<HTMLDivElement>(null)
  const [menuPos, setMenuPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 })

  // Close menu when clicking outside (accounts for portal)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      const inMenu = menuRef.current && menuRef.current.contains(target)
      const inAnchor = anchorRef.current && anchorRef.current.contains(target)
      if (!inMenu && !inAnchor) setIsOpen(false)
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleItemClick = (onClick: () => void) => {
    onClick()
    setIsOpen(false)
  }

  const openMenu = () => {
    setIsOpen((prev) => {
      const next = !prev
      if (!prev && anchorRef.current) {
        const rect = anchorRef.current.getBoundingClientRect()
        const menuWidth = 160 // w-40
        const menuHeight = items.length * 32 + 8 // Approximate height: items * 32px + padding
        const gap = 4
        const viewportHeight = window.innerHeight

        // Check if there's enough space below
        const spaceBelow = viewportHeight - rect.bottom
        const shouldShowAbove = spaceBelow < menuHeight + gap + 20 // 20px buffer

        const top = shouldShowAbove
          ? rect.top - menuHeight - gap
          : rect.bottom + gap

        setMenuPos({ top, left: Math.max(8, rect.right - menuWidth) })
      }
      return next
    })
  }

  // Reposition on resize/scroll while open
  useEffect(() => {
    if (!isOpen) return
    const onRecalc = () => {
      if (!anchorRef.current) return
      const rect = anchorRef.current.getBoundingClientRect()
      const menuWidth = 160
      const menuHeight = items.length * 32 + 8
      const gap = 4
      const viewportHeight = window.innerHeight

      const spaceBelow = viewportHeight - rect.bottom
      const shouldShowAbove = spaceBelow < menuHeight + gap + 20

      const top = shouldShowAbove
        ? rect.top - menuHeight - gap
        : rect.bottom + gap

      setMenuPos({ top, left: Math.max(8, rect.right - menuWidth) })
    }
    window.addEventListener('resize', onRecalc)
    window.addEventListener('scroll', onRecalc, true)
    return () => {
      window.removeEventListener('resize', onRecalc)
      window.removeEventListener('scroll', onRecalc, true)
    }
  }, [isOpen, items.length])

  return (
    <div className="relative flex items-center" ref={anchorRef}>
      <button
        onClick={openMenu}
        className="text-gray-500 rounded p-1 hover:bg-gray-100 transition-colors"
        aria-label="Actions"
      >
        <MoreVertical className="w-4 h-4" />
      </button>

      {isOpen && createPortal(
        <div
          ref={menuRef}
          style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, zIndex: 9999 }}
          className="w-40 bg-white rounded-md shadow-lg border border-gray-200"
        >
          <div className="py-1">
            {items.map((item, index) => {
              const Icon = item.icon
              const isDestructive = item.variant === 'destructive'
              return (
                <button
                  key={index}
                  className={`w-full flex items-center px-3 py-2 text-xs transition-colors ${
                    isDestructive 
                      ? 'text-red-600 hover:bg-red-50' 
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                  onClick={() => handleItemClick(item.onClick)}
                >
                  {Icon && <Icon className="w-3 h-3 mr-2" />}
                  {item.label}
                </button>
              )
            })}
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

export default ActionMenu
